import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { getCreator } from '../services/api';
import { ComicCard } from '../components/ComicCard';
import { toast } from 'sonner';

function CreatorPage() {
  const { creatorId } = useParams();
  const [creator, setCreator] = useState(null);
  const [comics, setComics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchCreator() {
      try {
        const res = await getCreator(creatorId);
        setCreator(res.data);
        setComics(res.data.comics || []);
      } catch (error) {
        console.error('Error fetching creator:', error);
        toast.error('Failed to load creator');
      } finally {
        setLoading(false);
      }
    }
    fetchCreator();
  }, [creatorId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-hot-rod border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-4">Creator not found</h1>
        <Link to="/creators" className="btn-hotrod"><span>Browse Creators</span></Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="creator-page">
      {/* Hero */}
      <div className="relative h-64 bg-gradient-to-r from-hot-rod/20 to-chrome-orange/20">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background" />
      </div>

      {/* Profile */}
      <div className="relative -mt-20 page-padding">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center md:items-end gap-6">
            <div className="w-40 h-40 rounded-full border-4 border-hot-rod overflow-hidden bg-surface flex-shrink-0">
              <img 
                src={creator.profile_picture || 'https://images.unsplash.com/photo-1649331593153-7575e5cd3c6e?w=200'} 
                alt={creator.username}
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center gap-3 justify-center md:justify-start">
                <h1 className="text-4xl md:text-5xl font-teko" data-testid="creator-name">{creator.username}</h1>
                <span className={creator.subscription_tier === 'contributor' ? 'badge-contributor' : 'badge-creator'}>
                  {creator.subscription_tier}
                </span>
              </div>
              <p className="text-text-secondary mt-2 max-w-xl">{creator.bio || 'Comic Creator'}</p>
              
              <div className="flex items-center gap-6 mt-4 justify-center md:justify-start text-text-secondary">
                <div className="text-center">
                  <p className="stat-number text-2xl text-white">{comics.length}</p>
                  <p className="text-xs">Comics</p>
                </div>
                <div className="text-center">
                  <p className="stat-number text-2xl text-chrome-orange">
                    ${(creator.total_donations_received || 0).toFixed(2)}
                  </p>
                  <p className="text-xs">Donations</p>
                </div>
              </div>
            </div>
          </div>

          {/* Donation CTA */}
          <div className="mt-8 card-hotrod p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Heart className="text-hot-rod" size={32} />
              <div>
                <p className="font-teko text-xl">Support {creator.username}</p>
                <p className="text-sm text-text-secondary">Add them to your monthly donation allocation</p>
              </div>
            </div>
            <Link to="/donations" className="btn-hotrod">
              <span>Manage Donations</span>
            </Link>
          </div>

          {/* Comics */}
          <div className="mt-12">
            <h2 className="text-3xl font-teko mb-6">Comics by {creator.username}</h2>
            {comics.length === 0 ? (
              <p className="text-text-secondary">No comics published yet</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {comics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreatorPage;
